/**
 * Traduit les erreurs de validation Pydantic (renvoyées par
 * `POST /dpe/calculate` en 422 `INVALID_PAYLOAD`) en messages par section,
 * pour mettre en lumière dans le formulaire les champs obligatoires manquants.
 *
 * Chaque erreur Pydantic a un `loc` (ex. `["meta","construction_year"]`,
 * `["walls",0,"material"]`, `["ecs"]`). On mappe `loc[0]` vers la section UI
 * et on produit un libellé FR lisible.
 */

export type PydanticError = {
  loc: (string | number)[];
  msg: string;
  type: string;
};

export type SectionKey =
  | "meta"
  | "walls"
  | "roof"
  | "low_floor"
  | "windows"
  | "doors"
  | "systems";

export type SectionErrors = Partial<Record<SectionKey, string[]>>;

// Sous-objet du payload (loc[0]) → section du formulaire. Les systèmes
// (chauffage / ECS / ventilation / clim / ENR) partagent une seule section.
const SECTION_OF: Record<string, SectionKey> = {
  meta: "meta",
  walls: "walls",
  roof: "roof",
  low_floor: "low_floor",
  windows: "windows",
  doors: "doors",
  heating: "systems",
  ecs: "systems",
  ventilation: "systems",
  cooling: "systems",
  renewable: "systems",
};

// Libellé FR par chemin de champ (index numériques retirés).
const FIELD_LABELS: Record<string, string> = {
  "meta.shab_m2": "Surface habitable",
  "meta.construction_year": "Année de construction",
  "meta.type": "Type de logement",
  "meta.department_code": "Département",
  "meta.inertia": "Inertie thermique",
  "walls.material": "Matériau",
  "walls.surface_m2": "Surface",
  "roof.material": "Type",
  "roof.surface_m2": "Surface",
  "low_floor.material": "Type",
  "low_floor.surface_m2": "Surface",
  "windows.glazing": "Vitrage",
  "windows.surface_m2": "Surface",
  "windows.orientation": "Orientation",
  "doors.door_type": "Type",
  "doors.surface_m2": "Surface",
  "heating.generator": "Générateur de chauffage",
  "ecs.generator": "Générateur d'eau chaude sanitaire",
  "ventilation.type": "Type de ventilation",
};

// Quand un sous-objet entier est absent (loc de longueur 1).
const WHOLE_SECTION_LABELS: Record<string, string> = {
  ecs: "Eau chaude sanitaire — à renseigner",
  ventilation: "Ventilation — à renseigner",
  heating: "Chauffage — à renseigner",
  walls: "Au moins un mur — à renseigner",
  windows: "Au moins une fenêtre — à renseigner",
};

// Préfixe d'item pour les listes (avec index).
const ITEM_PREFIX: Record<string, string> = {
  walls: "Mur",
  windows: "Fenêtre",
  doors: "Porte",
};

/**
 * Convertit le tableau `details` Pydantic en libellés groupés par section.
 * Robuste aux entrées inattendues : retourne `{}` si `details` n'est pas un
 * tableau, ignore les erreurs dont la section est inconnue, déduplique.
 */
export function parseValidationErrors(details: unknown): SectionErrors {
  if (!Array.isArray(details)) return {};

  const out: SectionErrors = {};
  const seen = new Set<string>();

  for (const raw of details) {
    const loc = (raw as PydanticError | null)?.loc;
    if (!Array.isArray(loc) || loc.length === 0) continue;

    const top = String(loc[0]);
    const section = SECTION_OF[top];
    if (!section) continue;

    let label: string;
    if (loc.length === 1) {
      label = WHOLE_SECTION_LABELS[top] ?? top;
    } else {
      const parts = loc.filter((p): p is string => typeof p === "string");
      const fieldKey = parts.join(".");
      const base = FIELD_LABELS[fieldKey] ?? parts[parts.length - 1] ?? top;
      const idx = loc.find((p): p is number => typeof p === "number");
      label =
        typeof idx === "number" && ITEM_PREFIX[top]
          ? `${ITEM_PREFIX[top]} n°${idx + 1} — ${base}`
          : base;
    }

    const dedupKey = `${section}::${label}`;
    if (seen.has(dedupKey)) continue;
    seen.add(dedupKey);

    (out[section] ??= []).push(label);
  }

  return out;
}

/** Nombre total de champs manquants, toutes sections confondues. */
export function countErrors(errors: SectionErrors): number {
  return Object.values(errors).reduce((sum, list) => sum + (list?.length ?? 0), 0);
}
